import { motion } from "framer-motion";
import {
  Search,
  BadgeCheck,
  Layers,
  Calendar,
  GraduationCap,
  TrendingUp,
  ShieldCheck,
  CalendarClock,
  LineChart,
  Check,
} from "lucide-react";
import { Reveal, Chapter, TrackCorner, EASE } from "./primitives";
import { useLanguage } from "../../i18n/LanguageContext";

const STAGE_ICONS = [Search, BadgeCheck, GraduationCap, Layers, Calendar, TrendingUp];
const EXPLAINER_ICONS = [ShieldCheck, CalendarClock, LineChart];

export const Problem = () => {
  const { t } = useLanguage();
  return (
  <section className="relative overflow-hidden bg-white py-24 lg:py-36" data-testid="problem-section">
    <div className="pointer-events-none absolute -right-28 bottom-0 text-kn-navy/[0.05]">
      <TrackCorner className="h-[360px] w-[360px] rotate-180" />
    </div>
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Chapter index="01" label={t.problem.chapter} />
      </Reveal>
      <div className="mt-10 grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-7" delay={0.1}>
          <h2 className="text-4xl font-semibold leading-[1.12] tracking-tight text-kn-navy sm:text-5xl">
            {t.problem.h2a}{" "}
            <span className="text-brand-gradient block">{t.problem.h2b}</span>
          </h2>
        </Reveal>
        <Reveal className="lg:col-span-5 lg:pt-3" delay={0.2}>
          <p className="text-base leading-relaxed text-kn-navy/65">
            {t.problem.body}
          </p>
          <p className="mt-8 border-l-2 border-kn-cyan pl-6 font-serif text-xl italic leading-relaxed text-kn-navy2">
            “{t.problem.quote}”
          </p>
        </Reveal>
      </div>

      <Reveal className="mt-16" delay={0.15}>
        <div className="relative h-64 overflow-hidden rounded-2xl lg:h-80" data-testid="problem-image">
          <img src="/images/problem-officials.jpg" alt="Match officials coordinating on the field" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-kn-navy/70 via-kn-navy/20 to-transparent" />
          <p className="absolute bottom-6 left-8 font-mono text-[11px] tracking-[0.28em] uppercase text-white/85">
            {t.problem.caption}
          </p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

export const Lifecycle = () => {
  const { t } = useLanguage();
  const L = t.lifecycle;
  return (
  <section id="platform" className="relative bg-kn-ice py-24 lg:py-32" data-testid="lifecycle-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Chapter index="02" label={L.chapter} />
        <h2 className="mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">
          {L.heading}
        </h2>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 sm:grid-cols-3 lg:grid-cols-6"
          data-testid="lifecycle-band"
        >
          {L.stages.map((s, i) => {
            const Icon = STAGE_ICONS[i];
            return (
            <div key={s.label} className="group flex flex-col items-center gap-3 bg-white px-4 py-8 transition-colors duration-500 hover:bg-kn-navy">
              <Icon className="h-6 w-6 text-kn-blue transition-colors duration-500 group-hover:text-kn-cyan" strokeWidth={1.25} />
              <span className="text-sm font-semibold text-kn-navy transition-colors duration-500 group-hover:text-white">
                {s.label}
              </span>
              <span className="font-mono text-[10px] text-kn-navy/35 transition-colors duration-500 group-hover:text-white/40">
                0{i + 1}
              </span>
            </div>
            );
          })}
        </div>
      </Reveal>

      <div className="mt-24 grid gap-x-16 lg:grid-cols-2">
        {L.stages.map((s, i) => {
          const Icon = STAGE_ICONS[i];
          return (
          <Reveal key={s.label} delay={(i % 2) * 0.08}>
            <div className="flex gap-6 border-t border-kn-navy/10 py-8" data-testid={`stage-${i}`}>
              <span className="font-mono text-xs text-kn-blue">0{i + 1}</span>
              <div>
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-kn-blue" strokeWidth={1.25} />
                  <h3 className="text-lg font-semibold text-kn-navy">{s.label}</h3>
                </div>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-kn-navy/60">{s.desc}</p>
              </div>
            </div>
          </Reveal>
          );
        })}
      </div>

      <div className="mt-24 grid gap-12 lg:grid-cols-2 lg:gap-20" data-testid="feature-columns">
        <Reveal>
          <ul className="space-y-5">
            {L.featuresLeft.map((f) => (
              <li key={f} className="flex items-start gap-4 border-b border-kn-navy/10 pb-5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-kn-cyan" strokeWidth={1.75} />
                <span className="text-base text-kn-navy/80">{f}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="space-y-5">
            {L.featuresRight.map((f) => (
              <li key={f} className="flex items-start gap-4 border-b border-kn-navy/10 pb-5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-kn-cyan" strokeWidth={1.75} />
                <span className="text-base text-kn-navy/80">{f}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      <div className="mt-24 grid gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 lg:grid-cols-3" data-testid="platform-explainers">
        {L.explainers.map((e, i) => {
          const Icon = EXPLAINER_ICONS[i];
          return (
          <motion.div
            key={e.title}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
            className="group bg-white p-10 transition-colors duration-500 hover:bg-kn-navy"
          >
            <Icon className="h-8 w-8 text-kn-blue transition-colors duration-500 group-hover:text-kn-cyan" strokeWidth={1} />
            <h3 className="mt-6 text-lg font-semibold text-kn-navy transition-colors duration-500 group-hover:text-white">
              {e.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-kn-navy/60 transition-colors duration-500 group-hover:text-white/60">
              {e.desc}
            </p>
          </motion.div>
          );
        })}
      </div>
    </div>
  </section>
  );
};
